import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Search, Copy, Facebook, Linkedin, MessageCircle, Users2, RefreshCw, MapPin, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import FbGroupShare from "@/components/FbGroupShare";
import { SocialPublishBadge } from "@/components/SocialPublishBadge";

const API = process.env.REACT_APP_BACKEND_URL;

const WHEN_TABS = [
  { v: "", l: "Tout" },
  { v: "today", l: "Aujourd'hui" },
  { v: "weekend", l: "Ce week-end" },
  { v: "week", l: "Cette semaine" },
  { v: "month", l: "Ce mois-ci" },
];
const CATS = [
  { v: "concert", l: "Concert" }, { v: "spectacle", l: "Spectacle" },
  { v: "marche", l: "Marché" }, { v: "animation", l: "Animation" },
  { v: "sport", l: "Sport" }, { v: "expo", l: "Exposition" },
  { v: "fete", l: "Fête" }, { v: "atelier", l: "Atelier" }, { v: "autre", l: "Autre" },
];

const fmtDate = (iso) => {
  try { return iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : ""; }
  catch { return ""; }
};
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const fmtLong = (iso) => { try { return cap(new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })); } catch { return ""; } };
const fmtTime = (iso) => { try { const d = new Date(iso); return (d.getHours() || d.getMinutes()) ? d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }).replace(":", "h") : ""; } catch { return ""; } };
const sameDay = (a, b) => a && b && a.slice(0, 10) === b.slice(0, 10);
function detailedDates(ev) {
  const s = ev.start_date, e = ev.end_date;
  if (!s) return "";
  const sd = fmtLong(s), st = fmtTime(s);
  if (e && !sameDay(s, e)) { const ed = fmtLong(e), et = fmtTime(e); return `Du ${sd}${st ? ` à ${st}` : ""} au ${ed}${et ? ` à ${et}` : ""}`; }
  const et = fmtTime(e || "");
  if (st && et && et !== st) return `${sd} de ${st} à ${et}`;
  if (st) return `${sd} à ${st}`;
  return sd;
}
function listDate(ev) {
  const s = ev.start_date, e = ev.end_date;
  if (!s) return "";
  if (e && !sameDay(s, e)) return `${fmtDate(s)} → ${fmtDate(e)}`;
  const st = fmtTime(s);
  return `${fmtDate(s)}${st ? ` · ${st}` : ""}`;
}

function EventSharePanel({ ev, onTracked }) {
  const [showGroups, setShowGroups] = useState(false);
  const shareUrl = `${API}/api/event-vitrine/${ev.slug || ev.id}`;
  const lines = [`🎉 ${ev.title}`];
  const dd = detailedDates(ev);
  if (dd) lines.push(`📅 ${dd}`);
  const loc = [ev.address, ev.city].filter(Boolean).join(", ");
  if (loc) lines.push(`📍 ${loc}`);
  if (ev.organizer) lines.push(`🏢 ${ev.organizer}`);
  if (ev.category_label) lines.push(`🎭 ${ev.category_label}`);
  if (ev.description) lines.push(`\n${ev.description.trim()}`);
  lines.push(`\n👉 Infos & agenda : ${shareUrl}`);
  const text = lines.join("\n");

  const track = async (net) => {
    try { const { data } = await api.post("/admin/social/track", { kind: "event", id: ev.id, network: net }); onTracked?.(ev.id, data.social_posts, data.social_counts); }
    catch (e) { void e; }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(`${text}\n${shareUrl}`); toast.success("Texte + lien copiés — prêts à coller !"); }
    catch { toast.message("Copie manuelle requise."); }
  };
  const openFb = () => { try { navigator.clipboard.writeText(text); } catch (e) { void e; } track("facebook"); window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank", "noopener"); };
  const openLinkedin = () => { try { navigator.clipboard.writeText(text); } catch (e) { void e; } track("linkedin"); toast.success("Texte copié — collez-le dans LinkedIn."); window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener"); };
  const openWa = () => { track("whatsapp"); window.open(`https://wa.me/?text=${encodeURIComponent(text + "\n" + shareUrl)}`, "_blank", "noopener"); };

  return (
    <div className="border-t border-slate-200 bg-slate-50 p-4" data-testid={`event-share-panel-${ev.id}`}>
      <div className="flex flex-wrap gap-2 items-center">
        <Button onClick={copy} className="btn-shine text-white" data-testid={`event-copy-${ev.id}`}><Copy size={14} className="mr-1.5" /> Copier texte + lien</Button>
        <button onClick={openFb} data-testid={`event-fb-${ev.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1877F2] text-white text-xs font-semibold hover:opacity-90"><Facebook size={14} /> Facebook</button>
        <button onClick={() => setShowGroups((v) => !v)} data-testid={`event-groups-${ev.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:opacity-90"><Users2 size={14} /> Groupes</button>
        <button onClick={openLinkedin} data-testid={`event-linkedin-${ev.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#0A66C2] text-white text-xs font-semibold hover:opacity-90"><Linkedin size={14} /> LinkedIn</button>
        <button onClick={openWa} data-testid={`event-whatsapp-${ev.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 text-white text-xs font-semibold hover:opacity-90"><MessageCircle size={14} /> WhatsApp</button>
        <a href={`/sortir/${ev.slug || ev.id}`} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#E05638]"><ExternalLink size={13} /> Voir la page</a>
      </div>
      {showGroups && <FbGroupShare text={text} shareUrl={shareUrl} cityHint={ev.city || ""} onShared={() => track("groupes")} />}
    </div>
  );
}

export default function AdminEventShare() {
  const [events, setEvents] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [when, setWhen] = useState("");
  const [city, setCity] = useState("");
  const [cat, setCat] = useState("");
  const [near, setNear] = useState("");
  const [locating, setLocating] = useState(false);
  const [openId, setOpenId] = useState("");

  useEffect(() => { api.get("/cities").then((r) => setCities(r.data || [])).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (when) p.set("when", when);
    if (city) p.set("city", city);
    if (cat) p.set("category", cat);
    if (near) p.set("near", near);
    try { const { data } = await api.get(`/public/events?${p.toString()}`); setEvents(data || []); }
    catch { setEvents([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [when, city, cat, near]);

  const locateMe = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non disponible sur cet appareil."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setNear(`${pos.coords.latitude},${pos.coords.longitude}`); setLocating(false); toast.success("Sorties les plus proches en premier"); },
      () => { setLocating(false); toast.error("Impossible de vous localiser."); },
      { timeout: 8000 }
    );
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return events;
    return events.filter((e) => `${e.title} ${e.city || ""} ${e.category_label || e.category || ""}`.toLowerCase().includes(t));
  }, [events, q]);

  const onTracked = (id, social_posts, social_counts) => {
    setEvents((list) => list.map((e) => (e.id === id ? { ...e, social_posts, social_counts } : e)));
  };

  return (
    <div data-testid="admin-event-share">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="admin-event-share-title">
            <CalendarDays className="text-[#E05638]" size={26} /> Publications des événements
          </h1>
          <p className="text-slate-500 text-xs md:text-sm mt-1">Choisissez n'importe quel événement puis partagez-le (Facebook, groupes, WhatsApp, LinkedIn) — même principe que les publications des pros.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-event-share-reload">
          <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>

      {/* Filtres identiques à la page « Sortir » */}
      <div className="mt-5 flex flex-wrap gap-2" data-testid="events-when-tabs">
        {WHEN_TABS.map((w) => (
          <button key={w.v || "all"} onClick={() => setWhen(w.v)} data-testid={`events-when-${w.v || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border ${when === w.v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>
            {w.l}
          </button>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button onClick={locateMe} disabled={locating} data-testid="events-nearby"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${near ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-200 text-slate-700 hover:border-emerald-400"}`}>
          <MapPin size={13} /> {locating ? "Localisation…" : near ? "Autour de moi ✓" : "Autour de moi"}
        </button>
        {near && <button onClick={() => setNear("")} data-testid="events-nearby-clear" className="text-xs text-slate-500 hover:text-[#E05638]">Réinitialiser</button>}
      </div>

      <div className="mt-3 grid md:grid-cols-3 gap-3">
        <Select value={city} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="events-city"><SelectValue placeholder="Toutes les villes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les villes</SelectItem>
            {cities.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={cat} onValueChange={(v) => setCat(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="events-cat"><SelectValue placeholder="Toutes catégories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes catégories</SelectItem>
            {CATS.map((c) => (<SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (titre, ville…)" className="pl-9" data-testid="admin-event-share-search" />
        </div>
      </div>
      <div className="mt-2 text-xs text-slate-500" data-testid="events-count">{loading ? "Chargement…" : `${filtered.length} événement${filtered.length > 1 ? "s" : ""}`}</div>

      {loading ? (
        <div className="text-slate-500 text-sm mt-6">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-6 text-slate-500 text-sm bg-white border border-dashed rounded-2xl p-8 text-center">Aucun événement ne correspond à vos critères.</div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="admin-event-share-list">
          {filtered.map((ev) => (
            <div key={ev.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`event-item-${ev.id}`}>
              <button onClick={() => setOpenId((v) => (v === ev.id ? "" : ev.id))} className="w-full p-3 flex items-center gap-3 text-left hover:bg-slate-50" data-testid={`event-toggle-${ev.id}`}>
                {ev.image
                  ? <img src={ev.image} alt="" className="w-16 h-16 rounded-lg object-cover bg-slate-100 shrink-0" />
                  : <div className="w-16 h-16 rounded-lg bg-slate-100 grid place-items-center text-slate-300 shrink-0"><CalendarDays size={22} /></div>}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate">{ev.title}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
                    {ev.city && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {ev.city}</span>}
                    {ev.start_date && <span>{listDate(ev)}</span>}
                    {(ev.category_label || ev.category) && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-semibold">{ev.category_label || ev.category}</span>}
                  </div>
                  <div className="mt-1.5"><SocialPublishBadge socialPosts={ev.social_posts} socialCounts={ev.social_counts} testid={`event-pub-${ev.id}`} /></div>
                </div>
                <span className="text-xs font-semibold text-[#E05638] shrink-0">{openId === ev.id ? "Fermer" : "Partager"}</span>
              </button>
              {openId === ev.id && <EventSharePanel ev={ev} onTracked={onTracked} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
